import { useEffect, useMemo } from 'react'

import { useFormik } from '@fourthwall/utils/dist/formik'

import { useMemberChargesSubscriptionsQuery } from '../../../hooks/useMemberChargesSubscriptionsQuery'
import type { useMemberQuery } from '../../../hooks/useMemberQuery'
import { useRequestCancelSubscriptionMutation } from '../../../hooks/useRequestCancelSubscriptionMutation'
import { formatCurrencyFromDecimal, formatCurrencyToDecimal } from '../../../utils'
import { getValidationSchema } from '../consts'
import type { FormValues } from '../types'
import { CancelRequestType, RefundRequestType } from '../types'

type Props = {
  member: NonNullable<ReturnType<typeof useMemberQuery>['memberQuery']['data']>
  onSuccess: (cancelRequestType?: CancelRequestType, refundAmount?: number) => void
}

export const useSubscriptionCancelModal = ({ member, onSuccess }: Props) => {
  const { memberChargesSubscriptionsQuery } = useMemberChargesSubscriptionsQuery([
    {
      member_id: member.id,
    },
    { page: 1, limit: 1 },
  ])

  const { requestCancelSubscriptionMutation } = useRequestCancelSubscriptionMutation()

  const lastCharge = memberChargesSubscriptionsQuery.data?.charges[0]

  const form = useFormik<FormValues>({
    initialValues: {
      cancelRequestType: CancelRequestType.Now,
      refundRequestType: RefundRequestType.full,
      customAmount: '0',
    },
    validationSchema:
      lastCharge?.refundRequest.type === 'Allowed'
        ? getValidationSchema(
            lastCharge.refundRequest.options.custom.minAmount,
            lastCharge.refundRequest.options.custom.maxAmount,
          )
        : undefined,
    enableReinitialize: true,
    onSubmit: (values) => {
      let refundAmount: number | undefined

      if (!lastCharge || values.refundRequestType === RefundRequestType.none) {
        return requestCancelSubscriptionMutation.mutate(
          [
            {
              memberId: member.id,
              type: values.cancelRequestType,
            },
          ],
          {
            onSuccess: () => {
              onSuccess(values.cancelRequestType, refundAmount)
            },
          },
        )
      }

      if (values.refundRequestType === RefundRequestType.custom) {
        refundAmount = formatCurrencyToDecimal(
          parseFloat(values.customAmount),
          lastCharge.currency.decimalPlaces,
        )
      }

      if (
        values.refundRequestType === RefundRequestType.full &&
        lastCharge.refundRequest.type === 'Allowed'
      ) {
        refundAmount = lastCharge.refundRequest.options.full.amount
      }

      if (
        values.refundRequestType === RefundRequestType.prorated &&
        lastCharge.refundRequest.type === 'Allowed' &&
        lastCharge.refundRequest.options.prorated.type === 'Allowed'
      ) {
        refundAmount = lastCharge.refundRequest.options.prorated.amount
      }

      requestCancelSubscriptionMutation.mutate(
        [
          {
            memberId: member.id,
            type: values.cancelRequestType,
            refundAmount,
          },
        ],
        {
          onSuccess: () => {
            onSuccess(values.cancelRequestType, refundAmount)
          },
        },
      )
    },
  })

  useEffect(() => {
    if (form.values.cancelRequestType === CancelRequestType.Now) {
      form.setFieldValue('refundRequestType', RefundRequestType.full)
    } else if (form.values.cancelRequestType === CancelRequestType.Schedule) {
      form.setFieldValue('refundRequestType', RefundRequestType.none)
    }
  }, [form.values.cancelRequestType])

  const refundAmount = useMemo(() => {
    if (
      lastCharge?.refundRequest.type !== 'Allowed' ||
      form.values.refundRequestType === RefundRequestType.none
    )
      return undefined

    if (
      form.values.refundRequestType === RefundRequestType.full &&
      lastCharge.refundRequest.options.full
    )
      return formatCurrencyFromDecimal(
        lastCharge.refundRequest.options.full.amount,
        lastCharge.currency.decimalPlaces,
      )

    if (
      form.values.refundRequestType === RefundRequestType.prorated &&
      lastCharge.refundRequest.options.prorated &&
      lastCharge.refundRequest.options.prorated.type === 'Allowed'
    )
      return formatCurrencyFromDecimal(
        lastCharge.refundRequest.options.prorated.amount,
        lastCharge.currency.decimalPlaces,
      )

    if (form.values.refundRequestType === RefundRequestType.custom)
      return parseFloat(form.values.customAmount)
  }, [lastCharge, form.values.refundRequestType, form.values.customAmount])

  return {
    form,
    refundAmount,
    lastCharge,
    isLoading: requestCancelSubscriptionMutation.isLoading,
    isFetching: memberChargesSubscriptionsQuery.isFetching,
  }
}
